import React, { useState, useEffect, useRef, useCallback } from 'react';

const EventPaymentPage = ({ registrationId, eventId }) => {
  const [step, setStep] = useState('loading'); // loading | form | processing | success | error | already_paid
  const [paymentData, setPaymentData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [cardError, setCardError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cardBrand, setCardBrand] = useState(null); // brand détecté via Stripe Elements

  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);

  useEffect(() => {
    initPayment();
  }, []);

  // Callback ref : appelé dès que le div est inséré dans le DOM
  const cardMountRef = useCallback((node) => {
    if (node && stripeRef.current && !cardElementRef.current) {
      mountCardElement(node);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toujours lire le token depuis le DOM au moment de l'appel (jamais en cache)
  const getCsrf = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

  // Attend que window.Stripe soit disponible (chargé via <script> dans le head)
  const waitForStripe = (maxWait = 5000) => new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const start = Date.now();
    const poll = setInterval(() => {
      if (window.Stripe) {
        clearInterval(poll);
        resolve(window.Stripe);
      } else if (Date.now() - start > maxWait) {
        clearInterval(poll);
        reject(new Error('Stripe.js n\'a pas pu être chargé. Veuillez recharger la page.'));
      }
    }, 50);
  });

  // Frais Stripe estimés selon le brand détecté
  // Amex = 2.5% + 0.25€ ; autres = 1.5% + 0.25€ (tarif EU)
  // Le pays d'émission (EU/non-EU) n'est connu qu'après traitement
  const computeFee = (base, brand) => {
    const rate = (brand === 'amex') ? 0.025 : 0.015;
    return Math.round((base * rate + 0.25) * 100) / 100;
  };

  const displayFee = paymentData
    ? computeFee(paymentData.base_amount, cardBrand)
    : 0;
  const displayTotal = paymentData
    ? Math.round((paymentData.base_amount + displayFee) * 100) / 100
    : 0;
  const feeLabel = cardBrand === 'amex'
    ? '2,5% + 0,25 € (American Express)'
    : cardBrand && cardBrand !== 'unknown'
      ? '1,5% + 0,25 € (carte EU estimé)'
      : '1,5% + 0,25 € (carte EU estimé)';
  const feeIsEstimate = cardBrand !== 'amex'; // Amex est certain, EU/non-EU reste estimé

  const initPayment = async () => {
    try {
      const res = await fetch('/api/v1/event_payment/create_intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        body: JSON.stringify({ registration_id: registrationId })
      });
      const data = await res.json();

      if (!data.success) {
        if (data.message?.includes('déjà payée') || data.message?.includes('déjà effectué')) {
          setStep('already_paid');
        } else {
          setErrorMsg(data.message || 'Erreur lors de la récupération du paiement.');
          setStep('error');
        }
        return;
      }

      // Initialiser Stripe.js (chargé via <script> dans le layout)
      let StripeJs;
      try {
        StripeJs = await waitForStripe();
      } catch (stripeErr) {
        setErrorMsg(stripeErr.message);
        setStep('error');
        return;
      }
      stripeRef.current = StripeJs(data.publishable_key);
      setPaymentData(data);
      setStep('form');
    } catch (e) {
      setErrorMsg('Impossible de se connecter au serveur.');
      setStep('error');
    }
  };

  const mountCardElement = (node) => {
    const elements = stripeRef.current.elements();
    const card = elements.create('card', {
      style: {
        base: {
          color: '#2c0e16',
          fontFamily: 'Arial, sans-serif',
          fontSize: '16px',
          '::placeholder': { color: '#9e6575' },
          iconColor: '#7a4050'
        },
        invalid: { color: '#dc2626', iconColor: '#dc2626' }
      },
      hidePostalCode: true
    });
    card.mount(node);
    card.on('ready', () => setCardReady(true));
    card.on('change', (e) => {
      setCardError(e.error ? e.error.message : '');
      setCardComplete(e.complete);
      setCardBrand(e.brand || null);
    });
    cardElementRef.current = card;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardElementRef.current || !stripeRef.current || !cardReady) return;
    setSubmitting(true);
    setErrorMsg('');

    const { error, paymentIntent } = await stripeRef.current.confirmCardPayment(
      paymentData.client_secret,
      { payment_method: { card: cardElementRef.current } }
    );

    if (error) {
      setErrorMsg(error.message);
      setSubmitting(false);
      return;
    }

    if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
      try {
        const res = await fetch('/api/v1/event_payment/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
          body: JSON.stringify({ registration_id: registrationId })
        });
        const data = await res.json();
        if (data.success) {
          setStep('success');
          setTimeout(() => {
            window.location.href = `/events/${paymentData.event.id}`;
          }, 3000);
        } else if (paymentIntent.status === 'processing') {
          // Payment still processing — show success anyway, webhook will finalize
          setStep('success');
          setTimeout(() => {
            window.location.href = `/events/${paymentData.event.id}`;
          }, 3000);
        } else {
          setErrorMsg(data.message || 'Confirmation serveur impossible.');
          setSubmitting(false);
        }
      } catch (fetchError) {
        // Network error on confirm call — payment already succeeded on Stripe side
        setStep('success');
        setTimeout(() => {
          window.location.href = `/events/${paymentData.event.id}`;
        }, 3000);
      }
    } else if (paymentIntent.status === 'requires_action') {
      // 3DS authentification en cours — Stripe.js gère normalement mais au cas où
      setErrorMsg('Authentification supplémentaire requise. Veuillez réessayer.');
      setSubmitting(false);
    } else {
      setErrorMsg(`Statut inattendu : ${paymentIntent.status}. Veuillez réessayer.`);
      setSubmitting(false);
    }
  };

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const pageStyle = {
    minHeight: '80vh',
    background: '#f7eef2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    fontFamily: 'Arial, sans-serif'
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(124,40,55,0.12)',
    padding: '40px',
    width: '100%',
    maxWidth: '520px'
  };

  const labelStyle = { color: '#7a4050', fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' };
  const inputWrap  = { background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.2)', borderRadius: '12px', padding: '14px 16px', marginBottom: '18px' };

  // ─── Render helpers ──────────────────────────────────────────────────────────

  const renderSpinner = (msg) => (
    <div className="event-mobile-shell event-payment-page" style={pageStyle}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>⏳</div>
        <p style={{ color: '#7a4050', fontWeight: '600' }}>{msg}</p>
      </div>
    </div>
  );

  if (step === 'loading')   return renderSpinner('Chargement du paiement…');
  if (step === 'processing') return renderSpinner('Traitement du paiement en cours…');

  if (step === 'success') return (
    <div className="event-mobile-shell event-payment-page" style={pageStyle}>
      <div className="event-payment-card" style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🎉</div>
        <h2 style={{ color: '#2c0e16', fontWeight: '800', marginBottom: '8px' }}>Paiement effectué !</h2>
        <p style={{ color: '#7a4050' }}>Vous êtes bien inscrit(e). Redirection en cours…</p>
      </div>
    </div>
  );

  if (step === 'already_paid') return (
    <div className="event-mobile-shell event-payment-page" style={pageStyle}>
      <div className="event-payment-card" style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
        <h2 style={{ color: '#2c0e16', fontWeight: '800', marginBottom: '8px' }}>Déjà payé</h2>
        <p style={{ color: '#7a4050', marginBottom: '24px' }}>Cette inscription a déjà été réglée.</p>
        {(paymentData?.event?.id || eventId) && (
          <a href={`/events/${paymentData?.event?.id || eventId}`}
             style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', padding: '12px 28px', borderRadius: '10px', textDecoration: 'none', fontWeight: '700' }}>
            Voir l'événement
          </a>
        )}
      </div>
    </div>
  );

  if (step === 'error') return (
    <div className="event-mobile-shell event-payment-page" style={pageStyle}>
      <div className="event-payment-card" style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>❌</div>
        <h2 style={{ color: '#2c0e16', fontWeight: '800', marginBottom: '8px' }}>Erreur</h2>
        <p style={{ color: '#7a4050', marginBottom: '24px' }}>{errorMsg}</p>
        <a href="/events" style={{ color: '#7c2837', fontWeight: '600' }}>← Retour aux événements</a>
      </div>
    </div>
  );

  // ─── Step: form ──────────────────────────────────────────────────────────────

  return (
    <div className="event-mobile-shell event-payment-page" style={pageStyle}>
      <div className="event-payment-card" style={cardStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '1.8rem' }}>
            💳
          </div>
          <h1 style={{ color: '#2c0e16', fontSize: '1.5rem', fontWeight: '800', margin: '0 0 4px' }}>
            Payer mon inscription
          </h1>
          <p style={{ color: '#9e6575', fontSize: '14px', margin: 0 }}>Paiement sécurisé via Stripe</p>
        </div>

        {/* Event recap */}
        {paymentData?.event && (
          <div style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.15)', borderRadius: '14px', padding: '18px', marginBottom: '28px' }}>
            <div style={{ fontWeight: '700', color: '#2c0e16', fontSize: '1rem', marginBottom: '6px' }}>
              🎟️ {paymentData.event.title}
            </div>
            <div style={{ color: '#7a4050', fontSize: '13px' }}>
              📅 {paymentData.event.start_date}
              {paymentData.event.location && <span> &nbsp;·&nbsp; 📍 {paymentData.event.location}</span>}
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(124,40,55,0.12)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#7a4050', fontSize: '14px' }}>Inscription</span>
                <span style={{ color: '#2c0e16', fontSize: '14px', fontWeight: '600' }}>
                  {parseFloat(paymentData.base_amount).toFixed(2)} €
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#9e6575', fontSize: '13px' }}>
                  Frais de traitement{feeIsEstimate ? ' (estimation)' : ''}
                  <span style={{ fontSize: '11px', display: 'block', color: '#b08090' }}>{feeLabel}</span>
                </span>
                <span style={{ color: '#9e6575', fontSize: '13px', fontWeight: '600', alignSelf: 'center' }}>
                  {feeIsEstimate ? '~' : ''}{displayFee.toFixed(2)} €
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px dashed rgba(124,40,55,0.15)' }}>
                <span style={{ color: '#7a4050', fontSize: '14px', fontWeight: '600' }}>Total débité{feeIsEstimate ? ' (estimé)' : ''}</span>
                <span style={{ color: '#2c0e16', fontSize: '1.6rem', fontWeight: '800' }}>
                  {feeIsEstimate ? '~' : ''}{displayTotal.toFixed(2)} €
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Card form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Carte bancaire</label>
            <div ref={cardMountRef} style={inputWrap} id="card-element" />
            {cardError && (
              <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '4px' }}>{cardError}</p>
            )}
          </div>

          {errorMsg && (
            <div style={{ background: '#fdf0f2', border: '1px solid rgba(168,56,78,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#7c2837', fontSize: '13px' }}>
              ❌ {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={!cardComplete || !cardReady || submitting}
            style={{
              width: '100%',
              padding: '15px',
              background: (cardComplete && cardReady && !submitting) ? 'linear-gradient(135deg,#7c2837,#a8384e)' : '#cca7b0',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              cursor: (cardComplete && cardReady && !submitting) ? 'pointer' : 'not-allowed',
              fontWeight: '800',
              fontSize: '16px',
              marginBottom: '16px'
            }}
          >
            {submitting ? '⏳ Traitement en cours…' : `💳 Payer ${paymentData ? `${feeIsEstimate ? '~' : ''}${displayTotal.toFixed(2)} €` : ''}`}
          </button>

          {/* Stripe badge */}
          <div style={{ textAlign: 'center', color: '#9e6575', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <svg width="40" height="16" viewBox="0 0 60 25" fill="none">
              <text x="0" y="18" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#635bff">stripe</text>
            </svg>
            Paiement sécurisé · Données chiffrées
          </div>
        </form>

        {/* Back link */}
        <div style={{ marginTop: '20px', textAlign: 'center', position: 'relative' }}>
          <CancelLink href={paymentData?.event ? `/events/${paymentData.event.id}` : '/events'} />
        </div>
      </div>
    </div>
  );
};

const CANCEL_TOOLTIP = "Le remboursement des frais d'inscription n'est pas automatique. Si les conditions de l'événement le prévoient, contactez directement l'organisateur pour convenir des modalités de restitution.";

const CancelLink = ({ href }) => {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <a
        href={href}
        style={{ color: '#9e6575', fontSize: '13px', textDecoration: 'none', borderBottom: '1px dashed #c8a0b0' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
      >
        ← Annuler et revenir à l'événement
      </a>
      {show && (
        <span className="event-payment-cancel-tooltip" style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#2c0e16',
          color: '#fff',
          fontSize: '12px',
          lineHeight: '1.5',
          padding: '10px 14px',
          borderRadius: '10px',
          width: '280px',
          textAlign: 'left',
          boxShadow: '0 4px 16px rgba(44,14,22,0.2)',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          ⚠️ {CANCEL_TOOLTIP}
          <span style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            border: '6px solid transparent',
            borderTopColor: '#2c0e16'
          }} />
        </span>
      )}
    </span>
  );
};

export default EventPaymentPage;
